import { useQuery } from "@tanstack/react-query";
import { callWithAuth, getAllLessons } from "@/api/Api";
import type { Chapter } from "@shared_types";

/**
 * Fetch chapter information (iOS `useChapterInfo`).
 *
 * Mirrors `client-native/src/features/feed/hooks/useChapterInfo.ts`: hits
 * `getAllLessons` (`/auth/chapterinfo`) and returns `response.chapter`, which
 * carries the REAL per-chapter numeric `videoLevel` / `videoLevelText`. The
 * feed payload (ChapterFeedV2) does not carry these, so this is the only way to
 * drive spelling gating and smart-distractor difficulty off real chapter data.
 *
 * Only fetches when enabled and the chapter id is valid, to avoid wasted calls.
 */
export function useChapterInfo(
  chapterId: number | null,
  enabled: boolean = true,
) {
  return useQuery<Chapter>({
    // Shared key with useOptimisticLessonCompletion's invalidation.
    queryKey: ["chapterInfo", chapterId],
    queryFn: async () => {
      if (chapterId === null) {
        throw new Error("Chapter ID is required");
      }
      const response = await callWithAuth(getAllLessons, {
        chapterId,
      });
      return response.chapter;
    },
    enabled: enabled && chapterId !== null && chapterId !== -1,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes (iOS parity)
  });
}
